import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { NAV, LOGO } from "../lib/site";

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [loc.pathname]);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-[100] transition-all duration-300 ${
        scrolled
          ? "bg-cream/95 backdrop-blur border-b border-tan/60"
          : "bg-cream/90 lg:bg-transparent lg:backdrop-blur-none backdrop-blur"
      }`}
      data-testid="main-nav"
      style={{ WebkitTapHighlightColor: "transparent" }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 h-20 lg:h-24 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 min-w-0" data-testid="nav-logo">
          <img src={LOGO} alt="Elenem Strategies" className="h-11 w-11 lg:h-14 lg:w-14 object-contain flex-shrink-0" />
          <div className="hidden sm:flex flex-col leading-none min-w-0">
            <span className="font-display text-base tracking-tight text-ink-soft truncate">
              Elenem <span className="italic text-cobalt">Strategies</span>
            </span>
            <span className="mt-1 font-sans text-[10px] lg:text-[11px] text-ink-soft/80 lowercase truncate">
              learner-centered solutions for the future of work
            </span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={`nav-link-${item.to.replace("/", "") || "home"}`}
              className={({ isActive }) =>
                `text-sm tracking-wide link-underline transition-colors ${
                  isActive ? "text-cobalt" : "text-ink hover:text-cobalt"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden -mr-2 p-3 text-ink relative z-[110] cursor-pointer"
          aria-label="Toggle menu"
          aria-expanded={open}
          data-testid="nav-mobile-toggle"
          style={{ touchAction: "manipulation", WebkitTapHighlightColor: "transparent" }}
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div
          className="lg:hidden bg-cream border-t border-tan/60 shadow-lg relative z-[105]"
          data-testid="mobile-menu"
          style={{ touchAction: "manipulation" }}
        >
          <nav className="flex flex-col px-6 py-4 gap-1">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`mobile-nav-link-${item.to.replace("/", "") || "home"}`}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `block text-base py-3 border-b border-tan/40 last:border-b-0 ${
                    isActive ? "text-cobalt" : "text-ink"
                  }`
                }
                style={{ touchAction: "manipulation" }}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
};

export default Navbar;
